import { lazy, useCallback, useEffect, useMemo, useState } from 'react'

import { IActionFormMap, IActionRepeaterValue, IActionSettings, IActionType } from '@makyoapp/makyo-common'

import { PLUGIN_ACTIONS } from '@/shared/action-plugin'

import { ActionFormContextV2, ActionTriggerSource } from './ActionFormContextV2'

const ActFormShowDialog = lazy(() => import('@/components/ActionV2/action-forms/alerts/alert.ActFormShowDialog'))
const ActFormShowSnackbar = lazy(() => import('@/components/ActionV2/action-forms/alerts/alert.ActFormShowSnackbar'))
const ActFormPushNotif = lazy(() => import('@/components/ActionV2/action-forms/alerts/alert.ActFormPushNotif')) // TODO
const ActFormComposeEmail = lazy(() => import('@/components/ActionV2/action-forms/comm/comm.ActFormComposeEmail'))
const ActFormMakePhoneCall = lazy(() => import('@/components/ActionV2/action-forms/comm/comm.ActFormMakePhoneCall'))
const ActFormSMS = lazy(() => import('@/components/ActionV2/action-forms/comm/comm.ActFormSMS'))
const ActFormWA = lazy(() => import('@/components/ActionV2/action-forms/comm/comm.ActFormWA'))
const ActFormDownload = lazy(() => import('@/components/ActionV2/action-forms/misc/misc.ActFormDownload'))
const ActFormClipboard = lazy(() => import('@/components/ActionV2/action-forms/misc/misc.ActFormClipboard'))
const ActFormPlaySound = lazy(() => import('@/components/ActionV2/action-forms/misc/misc.ActFormPlaySound'))
const ActFormOpenOverlay = lazy(() => import('@/components/ActionV2/action-forms/nav/nav.ActFormOpenOverlay'))
const ActFormGoToScreen = lazy(() => import('@/components/ActionV2/action-forms/nav/nav.ActFormGoToScreen'))
const ActFormShowDetailScreen = lazy(() => import('@/components/ActionV2/action-forms/nav/nav.ActFormShowDetailScreen'))
const ActFormOpenMenu = lazy(() => import('@/components/ActionV2/action-forms/nav/nav.ActFormOpenMenu'))
const ActFormOpenFile = lazy(() => import('@/components/ActionV2/action-forms/open/open.ActFormOpenFile'))
const ActFormOpenLink = lazy(() => import('@/components/ActionV2/action-forms/open/open.ActFormOpenLink'))
const ActFormOpenMap = lazy(() => import('@/components/ActionV2/action-forms/open/open.ActFormOpenMap'))
const ActFormDataAddRow = lazy(() => import('@/components/ActionV2/action-forms/data/data.ActFormAddRow'))
const ActFormDataUpdateRow = lazy(() => import('@/components/ActionV2/action-forms/data/data.ActFormUpdateRow'))
const ActFormToggleValue = lazy(() => import('@/components/ActionV2/action-forms/data/data.ActFormToggleValue'))
const ActFormIncrementValue = lazy(() => import('@/components/ActionV2/action-forms/data/data.ActFormIncrementValue'))
const ActFormOpenDetailForm = lazy(() => import('@/components/ActionV2/action-forms/form/form.ActFormOpenDetailForm'))
const ActFormOpenAddItemForm = lazy(() => import('@/components/ActionV2/action-forms/form/form.ActFromOpenAddItemForm'))
const ActFormIfElse = lazy(() => import('@/components/ActionV2/action-forms/conditional/conditional.ActFormIfElse'))
const ActFormSwitch = lazy(() => import('@/components/ActionV2/action-forms/conditional/conditional.ActFormSwitch'))
const ActFormTemporaryVariable = lazy(
  () => import('@/components/ActionV2/action-forms/advance-action/advance-action.ActFormTemporaryVariable')
)
const ActFormFilter = lazy(() => import('@/components/ActionV2/action-forms/logic/logic.ActFormFilter'))
const ActFormAddToCart = lazy(() => import('@/components/ActionV2/action-forms/data/data.actFormAddToCart'))
const ActFormGmail = lazy(() => import('@/components/ActionV2/action-forms/plugins/form.ActFormGmailPlugin'))
const ActFormGeneratePDF = lazy(() => import('@/components/ActionV2/action-forms/data/data.actFormGeneratePDF'))
const ActFormSetGlobalVariable = lazy(
  () => import('@/components/ActionV2/action-forms/data/data.ActFormSetGlobalVariable')
)
const ActFormClearGlobalVariable = lazy(
  () => import('@/components/ActionV2/action-forms/data/data.actFormClearGlobalVariable')
)
const ActFormWait = lazy(() => import('@/components/ActionV2/action-forms/misc/misc.ActFormWait'))

export const ActFormPlugin = lazy(() => import('@/components/ActionV2/action-forms/plugins/form.plugin'))

export const ActionFormMap: IActionFormMap = {
  // alerts
  [IActionType.ShowDialog]: ActFormShowDialog,
  [IActionType.ShowSnackbar]: ActFormShowSnackbar,
  [IActionType.PushNotification]: ActFormPushNotif,
  // comm
  [IActionType.ComposeEmail]: ActFormComposeEmail,
  [IActionType.MakePhoneCall]: ActFormMakePhoneCall,
  [IActionType.StartSMS]: ActFormSMS,
  [IActionType.StartWhatsApp]: ActFormWA,
  // misc
  [IActionType.Download]: ActFormDownload,
  [IActionType.Clipboard]: ActFormClipboard,
  [IActionType.PlaySound]: ActFormPlaySound,
  [IActionType.Wait]: ActFormWait,
  // nav
  [IActionType.OpenOverlay]: ActFormOpenOverlay,
  [IActionType.GoToScreen]: ActFormGoToScreen,
  [IActionType.ShowDetailsScreen]: ActFormShowDetailScreen,
  [IActionType.OpenMenu]: ActFormOpenMenu,
  // open
  [IActionType.OpenFile]: ActFormOpenFile,
  [IActionType.OpenLink]: ActFormOpenLink,
  [IActionType.OpenMap]: ActFormOpenMap,
  // data
  [IActionType.AddNewRow]: ActFormDataAddRow,
  [IActionType.UpdateRow]: ActFormDataUpdateRow,
  [IActionType.ToggleValue]: ActFormToggleValue,
  [IActionType.IncrementValue]: ActFormIncrementValue,
  [IActionType.AddToCart]: ActFormAddToCart,
  [IActionType.GeneratePDF]: ActFormGeneratePDF,

  // conditional
  [IActionType.ConditionalIf]: ActFormIfElse,
  [IActionType.ConditionalSwitch]: ActFormSwitch,
  // logic
  [IActionType.FilterData]: ActFormFilter,
  // Advance Action
  [IActionType.TemporaryVariable]: ActFormTemporaryVariable,

  // form
  [IActionType.FormUpdateItemScreen]: ActFormOpenDetailForm,
  [IActionType.FormAddItemScreen]: ActFormOpenAddItemForm,
  // plugins
  [PLUGIN_ACTIONS.GMAIL.type]: ActFormGmail,

  // clobal variable
  [IActionType.SetGlobalVariable]: ActFormSetGlobalVariable,
  [IActionType.ClearGlobalVariable]: ActFormClearGlobalVariable,
}

export const isActionHasSettings = (action: IActionType) => {
  return !!ActionFormMap[action]
}

export const isActionPlugin = (action: { settings?: IActionSettings }) => {
  return !!action.settings?.pluginAction
}

export const ActionFormProviderV2 = ({ children }: { children: React.ReactNode }) => {
  // Local State
  const [editedForm, setEditedForm] = useState<IActionRepeaterValue | null>(null)

  const [actionTriggerSource, setActionTriggerSource] = useState<ActionTriggerSource | null>(null)
  const [actionFormShowClearValue, setActionFormShowClearValue] = useState(false)

  // exposed functions
  const openActionForm = useCallback((action: IActionRepeaterValue, triggerSource?: ActionTriggerSource) => {
    setEditedForm(action)
    if (!!triggerSource) {
      setActionTriggerSource(triggerSource)
    }

    // show `clear value` data type on action input
    if (action.type === IActionType.UpdateRow) {
      setActionFormShowClearValue(true)
    }
  }, [])

  const closeActForm = useCallback(() => {
    setEditedForm(null)
    setActionTriggerSource(null)
    if (actionFormShowClearValue) {
      setActionFormShowClearValue(false)
    }
  }, [actionFormShowClearValue])

  useEffect(() => {
    if (!!editedForm) {
      return () => {
        setEditedForm(null)
      }
    }
  }, [editedForm])

  const value = useMemo(
    () => ({
      editedForm,
      setEditedForm,
      openActionForm,
      closeActForm,
      actionTriggerSource,
      actionFormShowClearValue,
      setActionFormShowClearValue,
    }),
    [editedForm, setEditedForm, openActionForm, closeActForm, actionTriggerSource, actionFormShowClearValue]
  )

  return <ActionFormContextV2.Provider value={value}>{children}</ActionFormContextV2.Provider>
}
